import {DEFAULT_BACKGROUND_COLOR} from '@cohort/shared/schema/common';
import type {Price} from '@cohort/shared/schema/common/currency';
import {removeUndefined} from '@cohort/shared/typeUtils';
import {formatAmount} from '@cohort/shared/utils/format';
import {CohortError} from '@cohort/shared-frontend/common/errors';
import {NULL_REF} from '@cohort/shared-frontend/common/React';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {isDark} from '@cohort/shared-frontend/utils/isDark';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {CampaignTos} from '@cohort/wallet/components/store/checkout/CampaignTos';
import {Checkbox} from '@cohort/wallet/components/store/checkout/Checkbox';
import useNotify from '@cohort/wallet/hooks/notify';
import useOrderSessionErrors from '@cohort/wallet/hooks/orderSessionErrors';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OrdersApi} from '@cohort/wallet/lib/Endpoints';
import {Colors} from '@cohort/wallet/lib/Theme';
import {poll} from '@cohort/wallet/lib/Utils';
import sendMessageToParent from '@cohort/wallet/lib/WindowMessaging';
import type {CampaignStoreWDto} from '@cohort/wallet-schemas/campaign';
import type {OrderWDto} from '@cohort/wallet-schemas/order';
import type {Stripe, StripeCardNumberElement} from '@stripe/stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import React, {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {styled} from 'styled-components';
import {shallow} from 'zustand/shallow';

const StripeInput = styled.div<{$backgroundColorCode: string}>`
  border-radius: 0.375rem /* 6px */;
  background-color: ${props =>
    isDark(props.$backgroundColorCode) ? 'rgb(255 255 255 / 0.05)' : 'rgb(0 0 0 / 0.05)'};

  width: 100%;
  min-height: 52px;
  box-sizing: border-box;
  border: solid 1px;
  padding: 16px;
  border-color: ${props =>
    isDark(props.$backgroundColorCode) ? 'rgb(255 255 255 / 0.1)' : 'rgb(0 0 0 / 0.1)'};
`;

type CreditCardFormProps = {
  campaign: CampaignStoreWDto;
  redeemCode?: string;
  successCallback: (order: OrderWDto) => void;
  price: Price;
};

export const CreditCardForm: React.FC<CreditCardFormProps> = ({
  campaign,
  redeemCode,
  successCallback,
  price,
}) => {
  const notify = useNotify();
  const handleOrderSessionErrors = useOrderSessionErrors();
  const [tosChecked, setTosChecked] = useState(false);

  const cardNumberRef = useRef<HTMLDivElement>(NULL_REF);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | undefined>(undefined);

  const cardExpiryRef = useRef<HTMLDivElement>(NULL_REF);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardExpiryError, setCardExpiryError] = useState<string | undefined>(undefined);

  const cardCvcRef = useRef<HTMLDivElement>(NULL_REF);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardCvcError, setCardCvcError] = useState<string | undefined>(undefined);

  const [cardElement, setCardElement] = useState<StripeCardNumberElement | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | undefined>();
  const createPaymentSession = useApi(OrdersApi.createPaymentSession);
  const findOrderByPaymentSessionId = useApi(OrdersApi.findOrderByPaymentSessionId);

  const {t} = useTranslation('components', {keyPrefix: 'store.checkout.creditCardForm'});
  const {hasDarkBg, backgroundColor} = useThemeContext();
  const stripePromise = useRef<Promise<Stripe | null> | null>(null);
  const {orderTokenId, clearOrderTokenId} = useAppStore(
    store => ({
      orderTokenId: store.orderTokenId,
      clearOrderTokenId: store.clearOrderTokenId,
    }),
    shallow
  );

  if (stripePromise.current === null) {
    const applyDarkBg = isDark(campaign.store.backgroundColorCode ?? DEFAULT_BACKGROUND_COLOR);
    const style = {
      base: {
        fontFamily: "'Inter', sans-serif",
        fontSize: '16px',
        color: applyDarkBg ? Colors.White : Colors.DarkGray,
      },
    } as const;

    stripePromise.current = loadStripe(
      campaign.mode === 'test'
        ? import.meta.env.COHORT_STRIPE_TEST_PUBLISHABLE_KEY
        : import.meta.env.COHORT_STRIPE_PUBLISHABLE_KEY
    );
    stripePromise.current
      .then(stripe => {
        if (!stripe || !cardNumberRef.current || !cardExpiryRef.current || !cardCvcRef.current) {
          return;
        }
        const elements = stripe.elements();

        const cardNumber = elements.create('cardNumber', {
          showIcon: true,
          iconStyle: 'solid',
          style,
        });
        cardNumber.mount(cardNumberRef.current);
        cardNumber.on('change', evt => {
          setCardNumberComplete(evt.complete);
          setCardNumberError(evt.error?.message);
        });
        setCardElement(cardNumber);

        const cardExpiry = elements.create('cardExpiry', {style});
        cardExpiry.mount(cardExpiryRef.current);
        cardExpiry.on('change', evt => {
          setCardExpiryComplete(evt.complete);
          setCardExpiryError(evt.error?.message);
        });

        const cardCvc = elements.create('cardCvc', {style});
        cardCvc.mount(cardCvcRef.current);
        cardCvc.on('change', evt => {
          setCardCvcComplete(evt.complete);
          setCardCvcError(evt.error?.message);
        });
      })
      .catch(err => notify('error', err));
  }

  const onSubmit = useCallback(async () => {
    setIsProcessing(true);
    setProcessingError(undefined);

    try {
      const paymentSession = await createPaymentSession(
        campaign.store.slug,
        price.currency,
        redeemCode,
        orderTokenId
      );
      const stripe = await stripePromise.current;

      if (!stripe || !cardElement) {
        notify('error', t('orderError'));
        return;
      }

      const {paymentIntent, error} = await stripe.confirmCardPayment(
        paymentSession.stripeClientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        },
        {
          handleActions: true,
        }
      );

      setProcessingError(error?.message);

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const order = await poll(
          async () => {
            const resp = await findOrderByPaymentSessionId(paymentSession.id);
            if (!resp.order) {
              throw new Error('Order not found');
            }
            return resp.order;
          },
          2 * 1000,
          50
        );
        clearOrderTokenId();
        successCallback(order);
      } else {
        sendMessageToParent({
          event: 'payment.failed',
          payload: {
            paymentSessionId: paymentSession.id,
          },
        });
      }
    } catch (err: unknown) {
      sendMessageToParent({
        event: 'order.error',
        payload: {
          error:
            err instanceof CohortError
              ? err.code
              : err instanceof Error
                ? err.message
                : String(err),
        },
      });
      handleOrderSessionErrors(err);
    } finally {
      setIsProcessing(false);
    }
  }, [
    createPaymentSession,
    campaign.store.slug,
    price.currency,
    redeemCode,
    orderTokenId,
    cardElement,
    notify,
    t,
    successCallback,
    findOrderByPaymentSessionId,
    handleOrderSessionErrors,
    clearOrderTokenId,
  ]);

  const canSubmit = useMemo(
    () => cardNumberComplete && cardExpiryComplete && cardCvcComplete,
    [cardCvcComplete, cardExpiryComplete, cardNumberComplete]
  );

  const errors = useMemo(
    () => removeUndefined([processingError, cardNumberError, cardExpiryError, cardCvcError]),
    [processingError, cardCvcError, cardExpiryError, cardNumberError]
  );

  return (
    <form id="payment-form" className="space-y-5">
      <div>
        <label
          htmlFor="card-number"
          className={classNames(
            'mb-1 block text-sm font-medium',
            hasDarkBg ? 'text-white/70' : 'text-black/70'
          )}
        >
          {t('cardNumber')}
        </label>
        <StripeInput
          ref={cardNumberRef}
          $backgroundColorCode={backgroundColor}
          className="my-input"
        />
      </div>
      <div className="flex w-full space-x-3 ">
        <div className="w-full">
          <label
            htmlFor="expiration-date"
            className={classNames(
              'mb-1 block text-sm font-medium',
              hasDarkBg ? 'text-white/70' : 'text-black/70'
            )}
          >
            {t('cardExpiration')}
          </label>
          <StripeInput ref={cardExpiryRef} $backgroundColorCode={backgroundColor} />
        </div>
        <div className="w-full">
          <label
            htmlFor="cvc"
            className={classNames(
              'mb-1 block text-sm font-medium',
              hasDarkBg ? 'text-white/70' : 'text-black/70'
            )}
          >
            {t('cardCvc')}
          </label>
          <StripeInput ref={cardCvcRef} $backgroundColorCode={backgroundColor} />
        </div>
      </div>
      {errors.length > 0 && (
        <Fragment>
          {errors.map(error => {
            return (
              <p className="mt-2 text-sm text-red-600" key={error}>
                {String(error)}
              </p>
            );
          })}
        </Fragment>
      )}
      <div className="mt-6 flex items-center">
        <Checkbox checked={tosChecked} setChecked={() => setTosChecked(!tosChecked)} testId="tos" />

        <CampaignTos campaign={campaign} checked={tosChecked} setChecked={setTosChecked} />
      </div>
      <PrimaryButton
        testId="pay"
        type="button"
        isLoading={isProcessing}
        text={String(
          t('pay', {
            amount: formatAmount(price.amount, price.currency),
          })
        )}
        className="w-full"
        onClick={onSubmit}
        disabled={!tosChecked || !canSubmit || isProcessing}
        tracking={{namespace: 'store.checkout.payByCard'}}
      />
    </form>
  );
};
